import { useEffect, useState } from "react";
import { appConfig } from "../../../configs";
import { useCheckoutPage, useMerchantApiQueryWithParams } from "../../../hooks";
import { IViewTransaction } from "../../../interfaces-and-types";
import { View } from "./View";
import { transactionType } from "../../../enums";

export interface ITransactionIFrameProps {
  // iframe tx verification props: sent back to verify txs
  tx_ref: string | null;
  tx_type: transactionType | null;

  // iframe control props
  closeIframe: () => void;
  closeIframeShowSuccessMessage: () => void;
  closeIframeShowFailedMessage: () => void;
  iframeUrl: string;
}
export interface ITransactionIFrameViewProps extends ITransactionIFrameProps {
  transactionIsProcessing: boolean;
}

export interface IIFrameRedirectMessage {
  channel: string;
  tx_ref: string;
  status: string;
  merchant_redirect_url: string | null;
}

// NOTE: start a timer to ensure that even if the iframe link breaks eg Provider does not redirect to us,
// we can query the backend for the tx status and update as necessary
// also add a SessionId generated together with a request/tx,
// that must be sent along with every tx status check request when using public key
export const TransactionIFrame = (props: ITransactionIFrameProps) => {
  const {
    closeIframeShowFailedMessage,
    closeIframeShowSuccessMessage,
    tx_type,
  } = props;

  const [redirectReceived, setRedirectReceived] = useState(false);
  const [transactionIsProcessing, setTransactionIsProcessing] = useState(false);

  // hide/show the switcher when user enters/leaves view
  const { showPaymentMethodSwitcher, hidePaymentMethodSwitcher } =
    useCheckoutPage();

  useEffect(() => {
    hidePaymentMethodSwitcher();

    return () => showPaymentMethodSwitcher();
  }, []);

  const {
    isSuccess,
    isFetching,
    data: transaction,
  } = useMerchantApiQueryWithParams<
    { tx_ref: string; tx_type: transactionType },
    IViewTransaction
  >(
    [
      "getTransactionByRef",
      {
        tx_ref: props.tx_ref!, // NOTE: use this to ensure that react query caches with the correct queryKey
        tx_type: tx_type!,
      },
    ],
    {
      enabled: redirectReceived,
      refetchInterval: 10000, // refetch every 10 seconds
    }
    // (urlTemplate) => urlTemplate.replace("tx_ref", props.tx_ref!)
  );

  // this comes into effect when tx is in processing state
  useEffect(() => {
    if (isSuccess) {
      console.log("running check on tx status, transaction: ", transaction);

      switch (transaction?.status) {
        case "successful":
          performSuccessEvents({
            channel: "switchAppRedirectMessage",
            merchant_redirect_url: transaction.redirect_url,
            status: transaction.status,
            tx_ref: transaction.tx_ref,
          });
          break;

        case "failed":
          performFailedEvents({
            channel: "switchAppRedirectMessage",
            merchant_redirect_url: transaction.redirect_url,
            status: transaction.status,
            tx_ref: transaction.tx_ref,
          });
          break;
      }
    }
  }, [isFetching, isSuccess]); // NOTE: isFetching is added to force a re-render

  // const [iframeLoading, setWaitForIframeLoad] = useState(true);
  // useEffect(() => {
  //   if (iframeLoading) {
  //     setTimeout(() => {
  //       setWaitForIframeLoad(false);
  //     }, 1000);
  //   }
  // }, [iframeLoading]);

  // close modal when transaction is completed and redirect to merchant
  const onPaymentSuccessful = () => {
    closeIframeShowSuccessMessage();
  };

  const onPaymentFailed = () => {
    closeIframeShowFailedMessage();
  };

  useEffect(() => {
    const checkForTransactionCompletion = function (e: any) {
      // if (e.origin !== "http://localhost:3000") return;

      const data = e.data;

      if (!data || data?.channel !== "switchAppRedirectMessage") return; // not from <IFramePaymentRedirectHandler />

      console.log("raw data from IFramePaymentRedirectHandler", data);

      setRedirectReceived(true);

      // if tx complete
      if (data.status === "successful") {
        performSuccessEvents(data);
        return;
      }

      if (data.status === "failed") {
        performFailedEvents(data);
        return;
      }

      setTransactionIsProcessing(true);
    };

    window.addEventListener("message", checkForTransactionCompletion, false);

    return () =>
      window.removeEventListener("message", checkForTransactionCompletion);
  }, []);

  const performSuccessEvents = (data: IIFrameRedirectMessage) => {
    onPaymentSuccessful();

    // get the tx from API and send to client modal

    setTimeout(() => {
      (window as any).parent.postMessage(
        {
          switchappId: "switchapp-inline-message",
          action: "callFunction",
          targetFunction: "onSuccess",
          params: data, // "payment.successful",
        },
        "*"
      );

      if (data.merchant_redirect_url)
        window.location.href = `${data.merchant_redirect_url}?tx_ref=${data.tx_ref}&status=${data.status}`;
    }, appConfig.merchantRedirectDelayMs); // redirect to merchant after a brief delay
  };

  const performFailedEvents = (data: IIFrameRedirectMessage) => {
    onPaymentFailed();

    // get the tx from API and send to client modal

    (window as any).parent.postMessage(
      {
        switchappId: "switchapp-inline-message",
        action: "callFunction",
        targetFunction: "onFailure",
        params: data, // "payment.failed",
      },
      "*"
    );
  };

  return <View {...props} transactionIsProcessing={transactionIsProcessing} />;
};
